import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { colors, courtColors } from "../ui/index.js";
/** How far either side of the rule's slot to show. */
const WINDOW_MIN = 120;
/** Cross-hatch (both diagonals) = "blocked", matching the session grid's Unbookable. */
const UNBOOKABLE_HATCH = `repeating-linear-gradient(45deg, ${colors.danger}66 0 1px, transparent 1px 6px), ` +
    `repeating-linear-gradient(-45deg, ${colors.danger}66 0 1px, transparent 1px 6px)`;
const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
};
/** How a block presents: tone (drives fill) + short "how long" label. */
function present(state) {
    if (state === "bulk")
        return { tone: "unbookable", label: "2+ wk", long: "Unbookable — booked 2+ weeks" };
    if (state === "prebooked-near")
        return { tone: "unbookable", label: "this wk", long: "Unbookable — booked this week (frees next)" };
    return { tone: "pending", label: "next wk", long: "Pending — free this week, booked next" };
}
export function BulkBookingGrid({ dayBlocks, totalCourts, ruleWindow, }) {
    if (totalCourts <= 0)
        return null;
    const rStart = toMin(ruleWindow.startTime);
    const rEnd = rStart + Math.max(0, ruleWindow.durationMin);
    const times = [...new Set(dayBlocks.map((b) => b.localTime))]
        .filter((t) => toMin(t) >= rStart - WINDOW_MIN && toMin(t) <= rEnd + WINDOW_MIN)
        .sort((a, b) => toMin(a) - toMin(b));
    if (times.length === 0)
        return null;
    const courts = Array.from({ length: Math.min(totalCourts, 8) }, (_, i) => i + 1);
    const blockAt = (court, time) => dayBlocks.find((b) => b.courtNumber === court && b.localTime === time);
    const durAt = (time) => dayBlocks.find((b) => b.localTime === time)?.durationMin ?? 60;
    const rowInRule = (time) => {
        const s = toMin(time);
        return rStart < s + durAt(time) && s < rEnd;
    };
    const cell = (court, time) => {
        const b = blockAt(court, time);
        const color = courtColors[court] ?? colors.textSecondary;
        if (!b) {
            return (_jsx("div", { style: { height: 22, borderRadius: 4, background: colors.surfaceSecondary } }, `${time}-${court}`));
        }
        const p = present(b.state);
        const fill = p.tone === "unbookable"
            ? { background: `${UNBOOKABLE_HATCH}, ${colors.dangerLight}`, borderLeft: `3px solid ${colors.danger}66` }
            : {
                background: `repeating-linear-gradient(-45deg, ${color}06, ${color}06 5px, ${color}14 5px, ${color}14 9px)`,
                borderLeft: `3px solid ${color}40`,
            };
        return (_jsx("div", { title: `court ${court} — ${p.long} (${b.occurrences.map((o) => `${o.date} ${o.taken ? "✓" : "·"}`).join(" ")})`, style: {
                ...fill,
                height: 22,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }, children: _jsx("span", { style: {
                    fontSize: "0.46rem",
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                    color: p.tone === "unbookable" ? colors.danger : color + "B0",
                }, children: p.label }) }, `${time}-${court}`));
    };
    const legend = (sw, label) => (_jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: 5 }, children: [sw, label] }));
    const hatchSwatch = (_jsx("span", { style: { width: 16, height: 12, borderRadius: 3, background: `${UNBOOKABLE_HATCH}, ${colors.dangerLight}`, display: "inline-block" } }));
    const stripeSwatch = (_jsx("span", { style: {
            width: 16,
            height: 12,
            borderRadius: 3,
            display: "inline-block",
            background: `repeating-linear-gradient(-45deg, ${colors.slate}10, ${colors.slate}10 4px, ${colors.slate}28 4px, ${colors.slate}28 8px)`,
        } }));
    return (_jsxs("div", { style: { overflowX: "auto", WebkitOverflowScrolling: "touch" }, children: [_jsxs("div", { style: {
                    display: "grid",
                    gridTemplateColumns: `52px repeat(${courts.length}, minmax(34px, 1fr))`,
                    gap: 3,
                    minWidth: 52 + courts.length * 38,
                    alignItems: "center",
                }, children: [_jsx("div", {}), courts.map((c) => (_jsxs("div", { style: { textAlign: "center", fontSize: "0.62rem", fontWeight: 700, color: courtColors[c] ?? colors.textSecondary }, children: ["C", c] }, c))), times.map((t) => {
                        const hot = rowInRule(t);
                        return (_jsxs(Row, { children: [_jsx("div", { style: {
                                        fontSize: "0.66rem",
                                        textAlign: "right",
                                        paddingRight: 5,
                                        fontVariantNumeric: "tabular-nums",
                                        color: hot ? colors.primary : colors.textTertiary,
                                        fontWeight: hot ? 700 : 400,
                                        borderRight: hot ? `2px solid ${colors.primary}` : "none",
                                    }, children: t }), courts.map((c) => cell(c, t))] }, t));
                    })] }), _jsxs("div", { style: { display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8, fontSize: "0.68rem", color: colors.textSecondary }, children: [legend(hatchSwatch, "Unbookable (2+ wk / this wk)"), legend(stripeSwatch, "Pending (booked next wk)"), legend(_jsx("span", { style: { width: 16, height: 12, borderRadius: 3, background: colors.surfaceSecondary, display: "inline-block" } }), "Free"), legend(_jsx("span", { style: { width: 3, height: 12, background: colors.primary, display: "inline-block" } }), "Your rule’s time")] })] }));
}
function Row({ children }) {
    return _jsx(_Fragment, { children: children });
}
